import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardBody, Col, Button, Spinner, Row, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl } from "../../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import moment from "moment";
import { exportToPDF } from "../../../Utils/pdfExportUtils";
import { excelExportUtils } from "../../../Utils/excelExportUtils";
import { useAuth } from "../../../Context/AuthContext";

interface CallHistory {
  id: string;
  start_time: string;
  end_time: string;
  customer: {
    mobile: string;
  };
  agent: {
    agent_name: string;
    id: string;
  };
  call_date: string;
  duration: number;
  duration_seconds?: number;
  customer_amount: number | string;
  agent_amount: number | string;
  formatted_duration: string;
  customer_amount_without_currency: number | string;
  agent_amount_without_currency: number | string;
}

interface Agent {
  id: number;
  agent_name: string;
}

interface CallHistoryWithFooter extends CallHistory {
  isFooter?: boolean;
}

const customStyles = {
  headCells: {
    style: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#308e87",
      backgroundColor: "#f8f9fa",
      borderBottom: "2px solid #308e87",
    },
  },
  rows: {
    style: {
      borderBottom: "1px solid #dee2e6",
    },
  },
  table: {
    style: {
      border: "1px solid #dee2e6",
      borderRadius: "8px",
    },
  },
};

const conditionalRowStyles = [
  {
    when: (row: any) => row.isFooter === true,
    style: {
      fontWeight: "800",
      backgroundColor: "#f8f9fa",
      color: "#308e87",
      borderTop: "2px solid #dee2e6",
      fontSize: "16px",
    },
  },
];

export default function VideoCallReport() {
  const token = localStorage.getItem("token");
  const { currency } = useCurrency();
  const { hasPermission } = useAuth();

  // State Management
  const [filterText, setFilterText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filteredItems, setFilteredItems] = useState<CallHistory[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // Debounce search input changes (350ms delay) to prevent focus loss and excess API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filterText);
    }, 350);
    return () => clearTimeout(timer);
  }, [filterText]);

  // Fetch Agent List on Mount
  useEffect(() => {
    const fetchAgentList = async () => {
      try {
        const result = await axios.get(`${baseUrl}agent/getall`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const activeAgents = (result.data || []).filter(
          (agent: any) => agent.is_deleted === 0 && agent.is_active === 1
        );
        setAgents(activeAgents);
      } catch (error) {
        console.error("Error fetching agents list:", error);
      }
    };
    fetchAgentList();
  }, [token]);

  // Main Data Fetcher (updates table quietly without unmounting input fields)
  const fetchData = useCallback(
    async (page = currentPage, currentPerPage = perPage) => {
      // Validate Date Range before sending to backend
      if (fromDate && toDate && fromDate > toDate) {
        toast.error("From date cannot be greater than To date.");
        return; // Prevent API request
      }

      setTableLoading(true);
      try {
        const response = await axios.get(`${baseUrl}video_call_history/getallreport`, {
          params: {
            page,
            per_page: currentPerPage,
            search: debouncedSearch.trim() !== "" ? debouncedSearch : undefined,
            from_date: fromDate ? fromDate.toLocaleDateString("en-CA") : undefined,
            to_date: toDate ? toDate.toLocaleDateString("en-CA") : undefined,
            agent_name: selectedAgent !== "" ? selectedAgent : undefined,
            sort_field: sortField,
            sort_direction: sortDir,
          },
          headers: { Authorization: `Bearer ${token}` },
        });

        setFilteredItems(response.data.data || []);
        setTotalRows(response.data.total || 0);
        setCurrentPage(response.data.current_page || 1);
      } catch (error) {
        console.error("Error fetching video call report data:", error);
      } finally {
        setTableLoading(false);
      }
    },
    [currentPage, perPage, debouncedSearch, fromDate, toDate, selectedAgent, sortField, sortDir, token]
  );

  // Sort Handler
  const handleSort = useCallback(
    (column: any, direction: "asc" | "desc") => {
      setSortField(column.sortField || "created_at");
      setSortDir(direction);
      setCurrentPage(1);
    },
    []
  );

  // Auto-Fetch when filters, pagination, date ranges, or debounced search changes
  useEffect(() => {
    fetchData(currentPage, perPage);
  }, [currentPage, perPage, selectedAgent, fromDate, toDate, debouncedSearch, sortField, sortDir]);

  // Pagination Handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  // Unpaginated Fetcher for PDF / Excel Export
  const fetchAllDataWithoutPagination = async () => {
    if (fromDate && toDate && fromDate > toDate) {
      toast.error("From date cannot be greater than To date.");
      return [];
    }

    try {
      const response = await axios.get(`${baseUrl}video_call_history/getallreportnopagination`, {
        params: {
          search: debouncedSearch.trim() !== "" ? debouncedSearch : undefined,
          from_date: fromDate ? fromDate.toLocaleDateString("en-CA") : undefined,
          to_date: toDate ? toDate.toLocaleDateString("en-CA") : undefined,
          agent_name: selectedAgent !== "" ? selectedAgent : undefined,
          sort_field: sortField,
          sort_direction: sortDir,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching all data for export:", error);
      return [];
    }
  };

  // Memoized Total Calculations
  const totals = useMemo(() => {
    let totalSeconds = 0;
    let totalCust = 0;
    let totalAgt = 0;

    filteredItems.forEach((row) => {
      const start = new Date(`1970-01-01T${row.start_time}`);
      const end = new Date(`1970-01-01T${row.end_time}`);
      const diff = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000));
      totalSeconds += diff;

      const custVal = parseFloat(
        String(row.customer_amount_without_currency ?? row.customer_amount ?? "0").replace(/[^0-9.]/g, "")
      );
      const agtVal = parseFloat(
        String(row.agent_amount_without_currency ?? row.agent_amount ?? "0").replace(/[^0-9.]/g, "")
      );

      totalCust += isNaN(custVal) ? 0 : custVal;
      totalAgt += isNaN(agtVal) ? 0 : agtVal;
    });

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    let formattedDuration = "";
    if (hours > 0) formattedDuration += `${hours}h `;
    if (minutes > 0) formattedDuration += `${minutes}m `;
    if (seconds > 0 || (hours === 0 && minutes === 0)) formattedDuration += `${seconds}s`;

    return {
      totalSeconds,
      formattedDuration: formattedDuration.trim(),
      totalCust: totalCust.toFixed(2),
      totalAgt: totalAgt.toFixed(2),
    };
  }, [filteredItems]);

  // Memoized Table Rows with Footer Summary ONLY ON THE VERY LAST PAGE
  const columnsData = useMemo<CallHistoryWithFooter[]>(() => {
    if (filteredItems.length === 0) return [];

    const totalPages = Math.ceil(totalRows / perPage) || 1;
    const isLastPage = currentPage >= totalPages;

    if (!isLastPage) {
      return filteredItems;
    }

    return [
      ...filteredItems,
      {
        isFooter: true,
        id: "footer",
        start_time: "",
        end_time: "",
        customer: { mobile: "" },
        agent: { agent_name: "", id: "footer" },
        call_date: "Total",
        duration: totals.totalSeconds,
        formatted_duration: totals.formattedDuration,
        customer_amount: totals.totalCust,
        agent_amount: totals.totalAgt,
        customer_amount_without_currency: totals.totalCust,
        agent_amount_without_currency: totals.totalAgt,
      },
    ];
  }, [filteredItems, totals, currentPage, perPage, totalRows]);

  // Memoized DataTable Columns Definition
  const columns = useMemo(() => {
    const currencyHeader = currency ? ` (${currency})` : "";
    return [
      {
        name: <div>Sl.No</div>,
        cell: (row: CallHistoryWithFooter, index: number) => {
          if (row.isFooter) return "";
          return (currentPage - 1) * perPage + index + 1;
        },
        width: "75px",
      },
      {
        name: <div>Customer Mobile</div>,
        sortable: true,
        sortField: "customer_mobile",
        cell: (row: CallHistoryWithFooter) => {
          if (row.isFooter) return "";
          return row.customer?.mobile || "N/A";
        },
      },
      {
        name: <div>Agent Name</div>,
        sortable: true,
        sortField: "agent_name",
        cell: (row: CallHistoryWithFooter) => {
          if (row.isFooter) return "";
          return row.agent?.agent_name || "N/A";
        },
      },
      {
        name: <div>Call Date</div>,
        sortable: true,
        sortField: "call_date",
        cell: (row: CallHistoryWithFooter) => {
          if (row.isFooter) return <>Total</>;
          const startFormatted = moment(row.start_time, "HH:mm:ss").format("hh:mmA");
          const endFormatted = moment(row.end_time, "HH:mm:ss").format("hh:mmA");
          return <>{`${row.call_date} ${startFormatted} - ${endFormatted}`}</>;
        },
      },
      {
        name: <div>Duration</div>,
        sortable: true,
        sortField: "duration_minutes",
        cell: (row: CallHistoryWithFooter) => {
          if (row.isFooter) return row.formatted_duration || `${row.duration} mins`;
          return row.formatted_duration || "N/A";
        },
      },
      {
        name: <div>Customer Amount{currencyHeader}</div>,
        sortable: true,
        sortField: "customer_amount",
        cell: (row: CallHistoryWithFooter) => {
          if (row.isFooter) return totals.totalCust;
          const val = row.customer_amount_without_currency ?? row.customer_amount;
          if (val === undefined || val === null) return "N/A";
          const cleanVal = String(val).replace(/[^0-9.]/g, "");
          return cleanVal !== "" ? cleanVal : "N/A";
        },
      },
      {
        name: <div>Agent Amount{currencyHeader}</div>,
        sortable: true,
        sortField: "agent_amount",
        cell: (row: CallHistoryWithFooter) => {
          if (row.isFooter) return totals.totalAgt;
          const val = row.agent_amount_without_currency ?? row.agent_amount;
          if (val === undefined || val === null) return "N/A";
          const cleanVal = String(val).replace(/[^0-9.]/g, "");
          return cleanVal !== "" ? cleanVal : "N/A";
        },
      },
    ];
  }, [currency, currentPage, perPage, totals]);

  // Shared Helper for PDF & Excel Exporters
  const prepareExportData = async () => {
    const rawData = await fetchAllDataWithoutPagination();
    const allData = Array.isArray(rawData) ? rawData : Object.values(rawData);

    if (!allData.length) {
      toast.warn("No video call report available for export.", {
        position: "top-right",
        autoClose: 1000,
      });
      return null;
    }

    let totalSeconds = 0;
    let totalCust = 0;
    let totalAgt = 0;

    const rows = allData.map((row: any, idx: number) => {
      const start = new Date(`1970-01-01T${row.start_time}`);
      const end = new Date(`1970-01-01T${row.end_time}`);
      const diff = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000));
      totalSeconds += diff;

      const custVal = parseFloat(
        String(row.customer_amount_without_currency ?? row.customer_amount ?? "0").replace(/[^0-9.]/g, "")
      );
      const agtVal = parseFloat(
        String(row.agent_amount_without_currency ?? row.agent_amount ?? "0").replace(/[^0-9.]/g, "")
      );

      totalCust += isNaN(custVal) ? 0 : custVal;
      totalAgt += isNaN(agtVal) ? 0 : agtVal;

      const startFormatted = moment(row.start_time, "HH:mm:ss").format("hh:mmA");
      const endFormatted = moment(row.end_time, "HH:mm:ss").format("hh:mmA");

      return {
        slNo: (idx + 1).toString(),
        mobile: row.customer?.mobile || "N/A",
        agentName: row.agent?.agent_name || "N/A",
        dateTime: `${row.call_date} ${startFormatted} - ${endFormatted}`,
        duration: row.formatted_duration || "N/A",
        custAmt: isNaN(custVal) ? "0.00" : custVal.toFixed(2),
        agtAmt: isNaN(agtVal) ? "0.00" : agtVal.toFixed(2),
      };
    });

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    let formattedDuration = "";
    if (hours > 0) formattedDuration += `${hours}h `;
    if (minutes > 0) formattedDuration += `${minutes}m `;
    if (seconds > 0 || (hours === 0 && minutes === 0)) formattedDuration += `${seconds}s`;

    return {
      rows,
      formattedTotalDuration: formattedDuration.trim(),
      totalCustomerAmount: totalCust.toFixed(2),
      totalAgentAmount: totalAgt.toFixed(2),
    };
  };

  const exportPDF = async () => {
    const exportData = await prepareExportData();
    if (!exportData) return;

    // Currency mapping to prevent PDF font encoding artifacts (e.g. ₹ -> Rs.)
    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      "€": "EUR",
      "£": "GBP",
      "¥": "YEN",
      "₽": "RUB",
      "₩": "KRW",
      "฿": "THB",
      "₪": "ILS",
      "₫": "VND",
      "₱": "PHP",
    };
    const pdfCurrency = currency ? currencyMap[currency] || currency : "";
    const pdfHeader = pdfCurrency ? ` (${pdfCurrency})` : "";

    const tableData = exportData.rows.map((r) => [
      r.slNo,
      r.mobile,
      r.agentName,
      r.dateTime,
      r.duration,
      r.custAmt,
      r.agtAmt,
    ]);

    const footerRow = [
      "",
      "",
      "",
      "Total",
      exportData.formattedTotalDuration,
      exportData.totalCustomerAmount,
      exportData.totalAgentAmount,
    ];

    exportToPDF({
      fileName: "Video_Call_Report",
      columns: [
        "Sl.No",
        "Customer Mobile",
        "Agent Name",
        "Call Date & Time",
        "Duration",
        `Customer Amount${pdfHeader}`,
        `Agent Amount${pdfHeader}`,
      ],
      data: tableData,
      emptyDataMessage: "No video call report available for export.",
      footerRow,
    });
  };

  const exportExcel = async () => {
    const exportData = await prepareExportData();
    if (!exportData) return;

    const excelHeader = currency ? ` (${currency})` : "";
    const custHeader = `Customer Amount${excelHeader}`;
    const agtHeader = `Agent Amount${excelHeader}`;

    const tableData = exportData.rows.map((r) => ({
      "Sl.No": r.slNo,
      "Customer Mobile": r.mobile,
      "Agent Name": r.agentName,
      "Call Date & Time": r.dateTime,
      Duration: r.duration,
      [custHeader]: r.custAmt,
      [agtHeader]: r.agtAmt,
    }));

    const footerRow = {
      "Sl.No": "",
      "Customer Mobile": "",
      "Agent Name": "",
      "Call Date & Time": "Total",
      Duration: exportData.formattedTotalDuration,
      [custHeader]: exportData.totalCustomerAmount,
      [agtHeader]: exportData.totalAgentAmount,
    };

    excelExportUtils(tableData, "Video_Call_Report", "Video Call Report", footerRow);
  };

  return (
    <>
      <Breadcrumbs pageTitle="Video Call Report" parent="Reports" title="Video Call Report" />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            <Row className="align-items-center justify-content-end g-2">
              <Col xl={2}>
                <div className="d-flex gap-2">
                  {hasPermission("video-call-report.download_pdf") && (
                    <Button
                      color="primary"
                      onClick={exportPDF}
                      className="d-flex justify-content-center align-items-center p-0"
                      style={{ width: "30px", height: "30px" }}
                      title="Export PDF"
                    >
                      <FaFilePdf size={16} />
                    </Button>
                  )}
                  {hasPermission("video-call-report.download_excel") && (
                    <Button
                      color="secondary"
                      onClick={exportExcel}
                      className="d-flex justify-content-center align-items-center p-0"
                      style={{ width: "30px", height: "30px" }}
                      title="Export Excel"
                    >
                      <FaFileExcel size={16} />
                    </Button>
                  )}
                </div>
              </Col>

              <Col xl={3}>
                <div className="d-flex align-items-center">
                  <Input
                    type="select"
                    value={selectedAgent}
                    onChange={(e) => {
                      setSelectedAgent(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">Filter by agent</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.agent_name}>
                        {agent.agent_name}
                      </option>
                    ))}
                  </Input>
                </div>
              </Col>

              <Col xl={4}>
                <div className="d-flex align-items-center gap-2">
                  <label className="mb-0 small fw-semibold">From:</label>
                  <div style={{ flex: 1 }}>
                    <DatePicker
                      selected={fromDate}
                      onChange={(date: Date | null) => {
                        if (date && toDate && date > toDate) {
                          toast.error("From date cannot be greater than To date.");
                          setFromDate(date);
                          return;
                        }
                        setFromDate(date);
                        setCurrentPage(1);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="Start date"
                      isClearable
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                  </div>

                  <label className="mb-0 small fw-semibold">To:</label>
                  <div style={{ flex: 1 }}>
                    <DatePicker
                      selected={toDate}
                      onChange={(date: Date | null) => {
                        if (fromDate && date && fromDate > date) {
                          toast.error("From date cannot be greater than To date.");
                          setToDate(date);
                          return;
                        }
                        setToDate(date);
                        setCurrentPage(1);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="End date"
                      isClearable
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                  </div>
                </div>
              </Col>

              <Col xl={3}>
                <div className="d-flex align-items-center">
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={filterText}
                    onChange={(e) => {
                      setFilterText(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </Col>
            </Row>

            <div className="table-responsive custom-scrollbar mt-3">
              <DataTable
                data={columnsData}
                columns={columns}
                highlightOnHover
                striped
                customStyles={customStyles}
                pagination
                paginationServer
                paginationPerPage={perPage}
                paginationTotalRows={totalRows}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                paginationDefaultPage={currentPage}
                responsive
                sortServer
                onSort={handleSort}
                conditionalRowStyles={conditionalRowStyles}
                progressPending={tableLoading}
                progressComponent={
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "300px", width: "100%" }}
                  >
                    <Spinner color="primary" />
                  </div>
                }
              />
            </div>
          </CardBody>
        </Card>
      </Col>

      <ToastContainer />
    </>
  );
}
